import { z } from "zod";

import { isPostalCodeValid } from "Forms/Utils/postalCodeUtils";
import type { CountryCodes, SavePartnerRequest } from "Infrastructure/Api/Api";
import {
  getTranslation,
  Resources,
} from "Infrastructure/Translations/Resources";
import {
  isCompanyNumberValid,
  isPhoneValid,
  isTaxNumberValid,
} from "Shared/Utils/validations";

export const partnerFormSchema = z
  .object({
    businessName: z
      .string()
      .min(1, { error: () => getTranslation(Resources.Validation.Required) }),
    superiorPartnerId: z.string().optional(),
    identificationNumber: z
      .string()
      .min(1, { error: () => getTranslation(Resources.Validation.Required) })
      .refine(isCompanyNumberValid, {
        error: () => getTranslation(Resources.Validation.InvalidCompanyNumber),
      }),
    vat: z
      .string()
      .min(1, { error: () => getTranslation(Resources.Validation.Required) })
      .refine(isTaxNumberValid, {
        error: () => getTranslation(Resources.Validation.InvalidTaxNumber),
      }),
    email: z
      .string()
      .min(1, { error: () => getTranslation(Resources.Validation.Required) })
      .check(
        z.email({
          error: () => getTranslation(Resources.Validation.InvalidEmail),
        }),
      ),
    phone: z
      .string()
      .min(1, { error: () => getTranslation(Resources.Validation.Required) })
      .refine(isPhoneValid, {
        error: () => getTranslation(Resources.Validation.InvalidPhone),
      }),
    address: z
      .string()
      .min(1, { error: () => getTranslation(Resources.Validation.Required) }),
    city: z
      .string()
      .min(1, { error: () => getTranslation(Resources.Validation.Required) }),
    postalCode: z
      .string()
      .min(1, { error: () => getTranslation(Resources.Validation.Required) }),
    country: z
      .string()
      .min(1, { error: () => getTranslation(Resources.Validation.Required) }),
    allowRetroactivePayment: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (data.postalCode && !isPostalCodeValid(data.postalCode, data.country)) {
      ctx.addIssue({
        code: "custom",
        message: getTranslation(Resources.Validation.InvalidPostalCode),
        path: ["postalCode"],
      });
    }
  });

export type PartnerFormData = z.infer<typeof partnerFormSchema>;

export function toSavePartnerRequest(
  formData: PartnerFormData,
): SavePartnerRequest {
  return {
    companyName: formData.businessName,
    companyNumber: formData.identificationNumber,
    taxNumber: formData.vat,
    email: formData.email,
    phone: formData.phone,
    address: {
      street: formData.address,
      postalCode: formData.postalCode,
      municipality: formData.city,
      countryCode: formData.country as CountryCodes,
    },
    allowRetroactivePayment: formData.allowRetroactivePayment,
    parentPartnerID: formData.superiorPartnerId || null,
  };
}
